import { useCallback, useMemo, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { serverTimestamp } from 'firebase/firestore'
import { functions } from '../config/firebase.js'

/**
 * Consolidated Access Management Hook for React 19+ and Firebase v11+
 * Handles all device access operations with unified error handling and optimized performance.
 */
export function useAccessManagement() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  
  // Memoized cloud function reference
  const accessActions = useMemo(() => 
    httpsCallable(functions, 'accessManagementActions'), [])
  
  // Centralized error processing with memoization
  const processError = useCallback((rawError, action, payload) => {
    const baseError = {
      id: crypto.randomUUID(),
      timestamp: serverTimestamp(),
      action,
      context: { action, payload },
      retryable: false,
      severity: 'medium',
    }

    if (rawError.code) {
      const userMessages = {
        'unauthenticated': 'Please sign in to continue.',
        'permission-denied': "You don't have permission for this action.",
        'not-found': 'Resource not found or has been removed.',
        'already-exists': 'This resource already exists.',
        'invalid-argument': 'Please check your input and try again.',
        'resource-exhausted': 'Too many requests. Please wait before trying again.',
        'unavailable': 'Service temporarily unavailable.',
        'deadline-exceeded': 'Operation timed out. Please try again.',
        'failed-precondition': 'Unable to complete this action.',
      }
      
      const retryableCodes = ['unavailable', 'deadline-exceeded', 'resource-exhausted']
      
      return {
        ...baseError,
        code: rawError.code,
        message: rawError.message,
        userMessage: userMessages[rawError.code] || 'An unexpected error occurred.',
        retryable: retryableCodes.includes(rawError.code),
      }
    }
    
    return {
      ...baseError,
      code: 'unknown',
      message: rawError.message || 'Unknown error',
      userMessage: 'Something went wrong. Please try again.',
      retryable: rawError.name === 'NetworkError',
    }
  }, [])

  
  // Optimized action executor with retry logic
  const executeAction = useCallback(async (action, deviceId, data = {}, options = {}) => {
    const { enableRetry = true, maxRetries = 3, showUserError = true } = options
    
    setIsLoading(true)
    setError(null)
    
    try {
      const result = await accessActions({ action, deviceId, data })
      setRetryCount(0)
      return result.data
      
    } catch (rawError) {
      const processedError = processError(rawError, action, { deviceId, data })
      
      if (enableRetry && processedError.retryable && retryCount < maxRetries) {
        setRetryCount(prev => prev + 1)
        const delay = 1000 * 2**retryCount + Math.random() * 1000
        
        setTimeout(() => {
          executeAction(action, deviceId, data, { ...options, enableRetry: retryCount < maxRetries - 1 })
        }, delay)
        
        return
      }
      
      if (showUserError) setError(processedError)
      if (import.meta.env.DEV) console.error('Access management error:', processedError)
      
      throw processedError
      
    } finally {
      setIsLoading(false)
    }
  }, [accessActions, processError, retryCount])

  // Action methods with useCallback optimization
  const removeAccess = useCallback((deviceId, targetUserId) =>
    executeAction('removeAccess', deviceId, { targetUserId }), [executeAction])

  const changePermission = useCallback((deviceId, targetUserId, newPermission) =>
    executeAction('changePermission', deviceId, { targetUserId, newPermission }), [executeAction])

  const approveAccessRequest = useCallback((requestId, grantedPermission) =>
    executeAction('approveAccessRequest', '', { requestId, grantedPermission }), [executeAction])

  const denyAccessRequest = useCallback((requestId, reason) =>
    executeAction('denyAccessRequest', '', { requestId, reason }), [executeAction])

  const revokeAccessRequest = useCallback((requestId) =>
    executeAction('revokeAccessRequest', '', { requestId }), [executeAction])

  const inviteUser = useCallback((deviceId, email, permission = 'user', message) =>
    executeAction('inviteUser', deviceId, { email, permission, message }), [executeAction])

  const acceptInvitation = useCallback((invitationId) =>
    executeAction('acceptInvitation', '', { invitationId }), [executeAction])

  const declineInvitation = useCallback((invitationId) =>
    executeAction('declineInvitation', '', { invitationId }), [executeAction])

  const revokeInvitation = useCallback((invitationId) =>
    executeAction('revokeInvitation', '', { invitationId }), [executeAction])

  const leaveDevice = useCallback((deviceId) =>
    executeAction('leaveDevice', deviceId, {}), [executeAction])

  const resetDevice = useCallback((deviceId, reason) =>
    executeAction('resetDevice', deviceId, { reason }), [executeAction])

  const clearError = useCallback(() => setError(null), [])
  
  const retry = useCallback(() => {
    if (error?.retryable) {
      setRetryCount(0)
      const { lastAction, lastDeviceId, lastData } = error.context || {}
      if (lastAction) executeAction(lastAction, lastDeviceId, lastData)
    }
  }, [error, executeAction])

  return {
    // Device operations
    removeAccess,
    removeUser: removeAccess, // Alias
    changePermission,
    leaveDevice,
    
    // Access requests  
    approveAccessRequest,
    denyAccessRequest,
    revokeAccessRequest,
    
    // Invitations
    inviteUser,
    acceptInvitation,
    declineInvitation,
    revokeInvitation,
    
    // System operations
    resetDevice,
    
    // State management
    isLoading,
    error,
    clearError,
    retry,
    retryCount,
  }
}
